import { Table, TDynamicTables } from "@/components/kit/Table/Table";
import { useQueryParams } from "@/hooks/general/useQueryParams";
import SvgIcon from "@/components/common/SvgIcon";
import classNames from "classnames";
import OrganizationRow from "./OrganizationRow";
import { TOrganization, TOrganizationParamsType } from "../../types";
import { ESort } from "@/types";

const defaultColumnConfig: TDynamicTables["fields"] = [
  {
    widths: 209,
    title: "Name",
    visibility: true,
    index: 0,
    orderBy: "name",
  },
  {
    widths: 180,
    title: "Portal link",
    visibility: true,
    index: 1,
  },
  {
    widths: 163,
    title: "Region",
    visibility: true,
    index: 2,
  },
  {
    widths: 170,
    title: "Owner",
    visibility: true,
    index: 3,
  },
  {
    widths: 112,
    title: "Admins",
    visibility: true,
    index: 4,
  },
  {
    widths: 150,
    title: "License status",
    visibility: true,
    index: 5,
  },
  {
    widths: 230,
    title: "License usage",
    visibility: true,
    index: 6,
  },
  {
    widths: 120,
    title: "Available",
    visibility: true,
    index: 7,
  },
  {
    widths: 180,
    title: "Active plan",
    visibility: true,
    index: 8,
  },
  {
    widths: 200,
    title: "Added date",
    visibility: true,
    index: 9,
  },

  {
    widths: 106,
    title: "",
    visibility: true,
    index: 10,
  },
];

interface DeviceGroupsTableProps {
  data: TOrganization[];
  loading: boolean;
}

const OrganizationTable = ({ data, loading }: DeviceGroupsTableProps) => {
  const { setParams, params } = useQueryParams<TOrganizationParamsType>();
  const columnWidths = defaultColumnConfig.map((elm) => elm.widths);

  return (
    <Table.Root
      defaultColumnConfig={defaultColumnConfig}
      columnConfig={defaultColumnConfig}
      columnWidths={columnWidths as string[]}
      columnControl={false}
    >
      <Table.Header>
        <Table.RowHeader>
          {defaultColumnConfig.map((elm, index) => (
            <Table.ColumnHeader
              name={elm.title}
              key={index}
              hasAction={elm.title !== ""}
              moveable={elm.title !== ""}
              hasColumnControl={false}
              index={elm.index}
            >
              <span
                title={elm.title}
                className="truncate !font-bold items-center gap-2"
              >
                {elm.title !== "" ? elm.title : ""}
              </span>
              {elm.orderBy && params.orderBy === elm.orderBy && (
                <SvgIcon
                  iconName="linearArrow"
                  fill={
                    params.orderBy === elm.orderBy
                      ? "fill-gray-500"
                      : "fill-gray-300"
                  }
                  className={classNames({
                    "rotate-180":
                      params.orderBy === elm.orderBy &&
                      params.orderDirection === "DESC",
                  })}
                />
              )}
              <Table.ColumnHeaderAction
                removeable={false}
                lists={
                  elm.orderBy
                    ? [
                        {
                          id: "ascending",
                          title: "Sort ascending",
                          icon: "sortFromBottomToTop",
                          visible: !(
                            params.orderDirection === "ASC" &&
                            params.orderBy === elm.orderBy
                          ),
                          onClick: () =>
                            setParams({
                              ...params,
                              orderBy: elm.orderBy as any,
                              orderDirection: ESort.ASC,
                            }),
                        },
                        {
                          id: "descending",
                          title: "Sort descending",
                          icon: "sortFromTopToBottom",
                          visible: !(
                            params.orderDirection === "DESC" &&
                            params.orderBy === elm.orderBy
                          ),
                          onClick: () =>
                            setParams({
                              ...params,
                              orderBy: elm.orderBy as any,
                              orderDirection: ESort.DESC,
                            }),
                        },
                      ]
                    : []
                }
                index={elm.index}
              />
            </Table.ColumnHeader>
          ))}
        </Table.RowHeader>
      </Table.Header>
      <Table.Body isLoading={loading} skeletonLength={6}>
        {data?.map((policy) => (
          <OrganizationRow key={policy?.id} organization={policy} />
        ))}
      </Table.Body>
    </Table.Root>
  );
};

export default OrganizationTable;
